import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Form,
  SelectPicker,
  Message,
  useToaster,
  Input,
  Toggle,
  CheckTreePicker,
} from "rsuite";
import EditIcon from "@rsuite/icons/Edit";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";
import { useAuth0 } from "@auth0/auth0-react";
import RemindFillIcon from "@rsuite/icons/RemindFill";
import { Button, Modal, Badge, SegmentedPicker } from "../../../../../ui";

const Textarea = React.forwardRef((props, ref) => (
  <Input {...props} as="textarea" ref={ref} />
));

export const UpdateIssue = ({ issue, update, setUpdate }) => {
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();
  const initFormSite = {
    id: issue.id,
    reportable: issue.reportable,
    report_users: issue.report_users ?? [],
    type: issue.type,
    notes: issue.notes,
    assigned: issue.assigned,
    diagnoses: issue.diagnoses,
    resolved: issue.resolved,
    report_id: issue.report_id,
    processor_type: issue.processor_type,
  };

  const toggleData = false;
  const [checkTreeData, setCheckTreeData] = useState([]);

  const selectData = [
    "OFFLINE",
    "MAGNET",
    "EDU",
    "Credentials",
    "Host Configuration",
  ].map((item) => ({
    label: item,
    value: item,
  }));

  const selectProcessor = ["MMB", "HHM"].map((item) => ({
    label: item,
    value: item,
  }));

  const assignedSelectData = [
    "Remote Tech Team",
    "Cust-Password",
    "Network Team",
    "Jerome",
    "Joe",
    "Customer - Jerome",
    "Customer - Joe",
  ].map((item) => ({
    label: item,
    value: item,
  }));

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [open, setOpen] = useState(false);
  const [formValue, setFormValue] = useState(initFormSite);
  const message = (
    <Message showIcon type="success" header="Success">
      You successfully updated {issue?.id}.
    </Message>
  );

  const flattenUsers = (selected = [], tree = []) => {
    const result = new Set();

    const recurse = (nodes) => {
      for (const node of nodes) {
        if (selected.includes(node.value)) {
          if (node.children) {
            // If a parent node is selected, add all leaf children
            node.children.forEach((child) => result.add(child.value));
          } else {
            // It's a leaf node
            result.add(node.value);
          }
        } else if (node.children) {
          // Parent not selected, but check its children
          recurse(node.children);
        }
      }
    };

    recurse(tree);
    return Array.from(result);
  };

  const getUsers = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "get",
        url: `/network/system/users/${issue.id}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const internalUsers = response.data[0]
        .filter((user) => user.class === "internal")
        .map((user) => ({
          label: (
            <div style={{ display: "inline-block" }}>
              {user.first_name} {user.last_name}
              <div style={{ fontSize: "0.75em", color: "#888" }}>
                {user.email_address}
              </div>
            </div>
          ),
          value: user.email_address,
        }));

      const externalUsers = response.data[0]
        .filter((user) => user.class === "external")
        .map((user) => ({
          label: (
            <div style={{ display: "inline-block" }}>
              {user.first_name} {user.last_name}
              <div style={{ fontSize: "0.75em", color: "#888" }}>
                {user.email_address}
              </div>
            </div>
          ),
          value: user.email_address,
        }));

      const treeData = [];

      if (internalUsers.length > 0) {
        treeData.push({
          label: "Avante Users",
          value: "internal",
          children: internalUsers,
        });
      }

      if (externalUsers.length > 0) {
        treeData.push({
          label: "External Users",
          value: "external",
          children: externalUsers,
        });
      }

      setCheckTreeData(treeData); // ✅ This triggers re-render
    } catch (error) {
      console.log(error);
      // setGetMetadataError(true);
    }
    // setLoadingMetadata(false);
  };

  const onSubmit = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "PUT",
        url: `/network/update/issues`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          issue: {
            ...formValue,
            report_users: flattenUsers(formValue.report_users, checkTreeData),
          },
        }),
      });
      // setResMsg(response.data);
      toaster.push(message, "topCenter");
      setUpdate(!update);
      handleClose();
    } catch (error) {
      console.error(error);
      toaster.push(errMessage, "topCenter");
    }
  };

  useEffect(() => {
    getUsers();
    setFormValue({
      id: issue.id,
      type: issue.type,
      notes: issue.notes,
      assigned: issue.assigned,
      diagnoses: issue.diagnoses,
      resolved: issue.resolved,
      report_id: issue.report_id,
      processor_type: issue.processor_type,
      reportable: issue.reportable,
      report_users: issue.report_users ?? [],
    });
  }, [issue]);

  useEffect(() => {
    console.log(checkTreeData);
  }, [checkTreeData]);

  return (
    <>
      <Button variant="secondary" size="small" onClick={handleOpen}>
        <EditIcon />
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        size="lg"
        title={
          <ModalTitle>
            <EditIcon style={{ marginRight: "0.5rem" }} />
            Edit Issue: <SystemBadge>{issue?.id}</SystemBadge>
          </ModalTitle>
        }
        showFooter={true}
        footerActions={
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onSubmit}>
              Save Changes
            </Button>
          </>
        }
      >
        <FormContainer
          fluid
          formValue={formValue}
          onChange={(formValue) => setFormValue(formValue)}
        >
          <Form.Group controlId="processor_type">
            <FormLabel>Processor</FormLabel>
            <Form.Control
              name="processor_type"
              accepter={SelectPicker}
              data={selectProcessor}
              searchable={false}
              block
            />
          </Form.Group>
          <Form.Group controlId="type">
            <FormLabel>Type</FormLabel>
            <Form.Control
              name="type"
              accepter={SelectPicker}
              data={selectData}
              searchable={false}
              block
            />
          </Form.Group>
          <FormSection>
            <FormLabel>Reportable</FormLabel>
            <SegmentedPicker
              options={[
                { eventKey: "false", label: "No" },
                { eventKey: "true", label: "Yes" },
              ]}
              activeKey={String(formValue.reportable)}
              onSelect={(key) =>
                setFormValue((prev) => ({
                  ...prev,
                  reportable: key === "true",
                }))
              }
              size="small"
            />
          </FormSection>
          {formValue.reportable ? (
            <Form.Group controlId="report_users">
              <FormLabel>Assigned Users</FormLabel>
              <Form.Control
                name="report_users"
                accepter={CheckTreePicker}
                data={checkTreeData}
                placeholder="Select Users"
                block
              />
            </Form.Group>
          ) : null}
          <FormSection>
            <FormLabel>Notes</FormLabel>
            {formValue.reportable && (
              <ReportWarning>
                <RemindFillIcon size="1.2em" color="#f5a623" />
                <WarningText>
                  Users will receive this note in their report
                </WarningText>
              </ReportWarning>
            )}
            <Form.Control name="notes" accepter={Textarea} />
          </FormSection>

          <FormSection>
            <FormLabel>Assigned To</FormLabel>
            <Form.Control
              name="assigned"
              placeholder={formValue.assigned || "Select assignee"}
              accepter={SelectPicker}
              data={assignedSelectData}
              searchable={false}
              block
            />
          </FormSection>

          <FormSection>
            <FormLabel>Diagnosis</FormLabel>
            <Form.Control
              name="diagnoses"
              accepter={Textarea}
              placeholder="Enter diagnosis details..."
            />
          </FormSection>

          <FormSection>
            <FormLabel>Resolved</FormLabel>
            <SegmentedPicker
              options={[
                { eventKey: "false", label: "No" },
                { eventKey: "true", label: "Yes" },
              ]}
              activeKey={String(formValue.resolved)}
              onSelect={(key) =>
                setFormValue((prev) => ({ ...prev, resolved: key === "true" }))
              }
              size="small"
            />
          </FormSection>
        </FormContainer>
      </Modal>
    </>
  );
};

const errMessage = (
  <Message showIcon type="error" header="Error">
    Something went wrong. Please try again.
  </Message>
);

// Styled Components
const FormContainer = styled(Form)`
  background: transparent !important;

  &::before,
  &::after {
    display: none !important;
  }

  .rs-form-control-wrapper {
    background: transparent !important;

    &::before,
    &::after {
      display: none !important;
    }
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
`;

const SystemBadge = styled(Badge)`
  margin-left: 0.5rem;
  font-size: 0.9rem;
`;

const FormSection = styled.div`
  margin-bottom: 1.5rem;
  background: transparent !important;
  position: relative;
  z-index: 1;

  .rs-form-group {
    margin-bottom: 0;
  }

  &::before,
  &::after {
    display: none !important;
  }

  .rs-form-control-wrapper::before,
  .rs-form-control-wrapper::after {
    display: none !important;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ReportWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(245, 166, 35, 0.15) !important;
  border: none;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  position: relative;
  z-index: 1;

  &::before,
  &::after {
    display: none !important;
  }

  * {
    position: relative;
    z-index: 2;
  }
`;

const WarningText = styled.span`
  font-size: 0.85rem;
  color: #f5a623;
  font-weight: 500;
`;
